import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import * as XLSX from 'xlsx';
import { ValidadoresHelper } from './../../../../helpers/validadores.helper';
import { FileUpload } from 'primeng/fileupload';
import { JwtHelper } from '../../../../helpers/jwtDecode.helper';
import { NotaFiscalService } from '../../../../services/nota-fiscal-servico.service';
import { ToastService } from '../../../../services/toast.service';
import { SpinnerService } from '../../../../services/spinner.service';
import { SeveridadeToast } from '../../../../models/enums/severidade-toast.enum';
import { Router } from '@angular/router';
import { AuthService } from '../../../../services/auth.service';

@Component({
  selector: 'app-pre-visualizar-planilha',
  templateUrl: './pre-visualizar-planilha.component.html',
  styleUrls: ['./pre-visualizar-planilha.component.scss']
})
export class PreVisualizarPlanilhaComponent implements OnInit {
  @Output() dadosProcessados = new EventEmitter<any[]>();
  @ViewChild('fileUpload') fileUpload!: FileUpload;

  uploadedFiles: any[] = [];
  fileData: any[] = [];
  columns: string[] = [];
  errorMessage: string = '';
  loading: boolean = false;
  
  nomeArquivo: string = '';
  tamanhoArquivo: number = 0;
  dadosPlanilha: any[] = [];

  constructor(
    private notaFiscalService: NotaFiscalService,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private router: Router,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.inicializarDadosExemplo();
  }

  private inicializarDadosExemplo(): void {
    this.nomeArquivo = 'planilha_nfse_exemplo.xlsx';
    this.tamanhoArquivo = 10240;
    this.dadosPlanilha = [
      { data: '2024-01-15', descricao: 'NFS-e 001 - Serviços de Consultoria', valor: 1500.00, tipo: 'NFS-e' },
      { data: '2024-01-16', descricao: 'NFS-e 002 - Serviços de Auditoria', valor: 2500.00, tipo: 'NFS-e' },
      { data: '2024-01-17', descricao: 'NFS-e 003 - Serviços de Assessoria', valor: 1800.00, tipo: 'NFS-e' }
    ];
  }

  onUpload(event: any): void {
    this.loading = true;
    this.errorMessage = '';
    
    try {
      const file = event.files[0];
      if (!file) {
        this.errorMessage = 'Nenhum arquivo selecionado.';
        this.loading = false;
        return;
      }

      this.nomeArquivo = file.name;
      this.tamanhoArquivo = file.size;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            this.errorMessage = 'A planilha deve conter pelo menos um cabeçalho e uma linha de dados.';
            this.loading = false;
            return;
          }

          const headers = jsonData[0] as string[];
          this.columns = headers;

          this.dadosPlanilha = this.processarDadosPlanilha(jsonData.slice(1), headers);
          
          this.loading = false;
          this.toastService.setMensagemToast(
            'Planilha carregada com sucesso!',
            SeveridadeToast.Success
          );
        } catch (error) {
          this.errorMessage = 'Erro ao processar a planilha. Verifique se o arquivo é válido.';
          this.loading = false;
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      this.errorMessage = 'Erro ao carregar o arquivo.';
      this.loading = false;
    }
  }

  private processarDadosPlanilha(data: any[], headers: string[]): any[] {
    return data.map((row: any, index: number) => {
      const processedRow: any = {};
      
      headers.forEach((header: string, colIndex: number) => {
        const value = row[colIndex] || '';
        
        if (header.toLowerCase().includes('data') || header.toLowerCase().includes('date')) {
          processedRow.data = this.formatarData(value);
        } else if (header.toLowerCase().includes('descrição') || header.toLowerCase().includes('descricao') || header.toLowerCase().includes('description')) {
          processedRow.descricao = value;
        } else if (header.toLowerCase().includes('valor') || header.toLowerCase().includes('value') || header.toLowerCase().includes('amount')) {
          processedRow.valor = this.parseValor(value);
        } else if (header.toLowerCase().includes('tipo') || header.toLowerCase().includes('type')) {
          processedRow.tipo = value || 'NFS-e';
        } else {
          processedRow[header] = value;
        }
      });
      
      return processedRow;
    }).filter(row => row.descricao && row.valor); 
  }

  private formatarData(data: any): string {
    if (!data) return '';
    
    try {
      const date = new Date(data);
      if (isNaN(date.getTime())) return data.toString();
      
      return date.toLocaleDateString('pt-BR');
    } catch {
      return data.toString();
    }
  }

  private parseValor(valor: any): number {
    if (typeof valor === 'number') return valor;
    
    if (typeof valor === 'string') {
      const cleanValue = valor.replace(/[^\d,.-]/g, '');
      const numericValue = cleanValue.replace(',', '.');
      const parsed = parseFloat(numericValue);
      return isNaN(parsed) ? 0 : parsed;
    }
    
    return 0;
  }

  formatarTamanhoArquivo(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  applyFilterGlobal(event: any): void {

  }

  importarPlanilha(): void {
    if (this.dadosPlanilha.length === 0) {
      this.toastService.setMensagemToast(
        'Nenhum dado válido para importar.',
        SeveridadeToast.Error
      );
      return;
    }

    this.spinnerService.show();
    
    setTimeout(() => {
      this.dadosProcessados.emit(this.dadosPlanilha);
      this.spinnerService.hide();
      this.toastService.setMensagemToast(
        'Planilha importada com sucesso!',
        SeveridadeToast.Success
      );
    }, 2000);
  }

  cancelar(): void {
    this.router.navigate(['/importacao-nfse-emitidas']);
  }
}
